<template>
	<div ref="lineage-container" class="flow-lineage-container">
		<div class="flow-lineage-header">
			<slot name="header" />
		</div>
		<div :id="id" ref="lineage" class="flow-lineage-chart" />
		<div ref="lineage-popover">
			<slot v-if="popoverDataNode.id" name="popover" :d="popoverDataNode" :close="togglePopOver" />
		</div>
		<div :open="overlay" class="node-lineage-overlay" @click.stop="togglePopOver($event)" />
	</div>
</template>
<script>
/* eslint-disable vue/no-unused-components */
import * as d3 from "d3";
import Node from "./node-types/Node.vue";
import Vue, { defineComponent } from "vue";
import { createPopper } from "@popperjs/core";
import { renderScrollbars } from "./scrollbars";
import { buildNodes, toggleChildren, computeChilds, buildLevel } from "./nodes";
import { highlightPath, removeHighlight } from "./highlight";
import { renderLinks, buildLinks, diagonal } from "./links";
import zoom from "./zoom";

export default defineComponent({
	name: "Lineage",
	components: {
		Node
	},
	props: {
		/**
		 * Size of node
		 * { width: Number. height: Number}
		 */
		nodeSize: {
			type: Object,
			default: () => {
				return {
					width: 300,
					height: 50
				};
			}
		},
		/**
		 * Size of child node
		 * { width: Number. height: Number}
		 */
		childNodeSize: {
			type: Object,
			default: () => {
				return {
					width: 300,
					height: 36
				};
			}
		},
		/**
		 * Gap between nodes (horizontal and vertical)
		 * [Number.Number]
		 */
		gap: {
			type: Array,
			default: () => [150, 50]
		},
		/**
		 * max child to display
		 */
		maxChildren: {
			type: Number,
			default: 6
		},
		/**
		 * root object/node which is plotted at center and rest of the nodes are plotted on either left or right based on following format.
		 *
		 * <code>
		 * <pre>
		 * {
		 * 		id : String,
		 * 		name: String,
		 * 		from :[...],
		 * 		to: [...],
		 * 		children: [...],
		 * 		links : {
		 * 			from:[
		 * 				{
		 * 					id: String,
		 * 				}, ...
		 * 			],
		 * 			to:[
		 * 				{
		 * 					id: String,
		 * 				}, ...
		 * 			]
		 * 		}
		 * }
		 * </pre>
		 * </code>
		 */
		root: {
			type: Object,
			// eslint-disable-next-line @typescript-eslint/no-empty-function
			default: () => {}
		},
		/**
		 * Event to highlight node path
		 */
		highlightPathOn: {
			type: String,
			default: "click",
			validator(value) {
				return ["click", "hover"].includes(value);
			}
		}
	},
	data() {
		return {
			id: "lineageChart",
			size: {
				width: 1500,
				height: 800
			},
			nodeComponent: null,
			container: null,
			highlightedNodeId: null,
			overlay: false,
			popoverDataNode: {},
			pagination: () => {
				return {
					offset: 0,
					max: 6,
					get paginatedChild() {
						if (this.children && this.children.length <= this.max) {
							return this.children;
						} else {
							// console.log(this.offset, this.max, this.children ? this.children.length : 0);
							if (this.children && this.offset >= this.children.length) {
								this.offset -= this.max;
							}
							if (this.offset < 0) {
								this.offset = 0;
							}
							if (this.children && this.children.length - this.offset < this.max) {
								this.offset = this.children.length - this.max;
							}

							return this.children ? this.children.slice(this.offset, this.offset + this.max) : [];
						}
					}
				};
			}
		};
	},
	watch: {
		root: {
			handler: "init",
			deep: true
		},
		gap: {
			handler: "init",
			deep: true
		},
		nodeSize: {
			handler: "init",
			deep: true
		}
	},
	mounted() {
		/**
		 * Initialize chart
		 */
		this.init();

		window.addEventListener("resize", () => {
			this.init();
		});
	},
	beforeMount() {
		/**
		 * Generate and assign id to chart
		 */
		this.id = this.generateId();

		/**
		 * Register component for Node
		 */
		this.nodeComponent = Vue.extend(Node);
	},
	methods: {
		renderScrollbars,
		/**
		 * It will build array of Nodes with co-ordinates
		 */
		buildNodes,
		highlightPath,
		renderLinks,
		buildLinks,
		removeHighlight,
		...zoom,
		diagonal,
		toggleChildren,
		computeChilds,
		buildLevel,
		initializeContainer() {
			/**
			 * Calculate width and height
			 */

			this.size.width = this.$refs.lineage.offsetWidth;
			this.size.height = this.$refs.lineage.offsetHeight;

			const margin = { top: 0, right: 0, bottom: 0, left: 0 };
			const width = this.size.width - margin.left - margin.right;
			const height = this.size.height - margin.top - margin.bottom;
			const wrapper = document.getElementById(this.id);
			wrapper.innerHTML = "";
			this.svg = d3
				.select(`#${this.id}`)
				.append("svg")
				.attr("class", "lineage-svg")
				.attr("width", width + margin.right + margin.left)
				.attr("height", height + margin.top + margin.bottom)
				.on("click", () => {
					if (d3.event.target.classList.contains("lineage-svg")) {
						this.removeHighlight();

						this.togglePopOver();
					}
				});
			const container = this.svg
				.append("g")
				.attr("transform", `translate(${margin.left},${margin.top})`);

			this.svg
				.call(
					d3
						.zoom()
						.scaleExtent([0.3, 4])
						.on("zoom", () => {
							this.zoomContainer(d3.event.transform);
							if (d3.event.sourceEvent.type === "mousemove" && this.isSafari()) {
								const t = d3.zoomTransform(this.container.node());
								t.k += 0.0001;
								this.zoomContainer(t);
							}
						})
				)
				.on("dblclick.zoom", null);

			return container;
		},

		init(update = false) {
			let container = null;
			if (update && this.container) {
				container = this.container;
			} else {
				container = this.initializeContainer();
				this.container = container;
			}
			this.nodes = this.buildNodes(this.root);
			// console.log(nodes);
			container.selectAll("*").remove();
			container
				.append("svg:defs")
				.append("svg:marker")
				.attr("id", "dot")
				.attr("viewBox", "0 0 8 8")
				.attr("refX", 4)
				.attr("refY", 4)
				.attr("markerWidth", 4)
				.attr("markerHeight", 4)
				.attr("orient", "auto")
				.append("svg:circle")
				.attr("fill", "var(--dashboard-font-light)")
				.attr("r", 4)
				.attr("cx", 4)
				.attr("cy", 4);

			this.renderLinks(container);

			/** Start : plotting nodes */
			let i = 0;
			const node = container.selectAll("g.node").data(this.nodes, function (d) {
				return d.id || (d.id = ++i);
			});
			/**
			 * add all nodes in svg and binding click event
			 */
			const nodeEnter = node
				.enter()
				.append("g")
				.attr("class", d => {
					let c = "node c-node";
					if (d.isChildNode) {
						c += ` child-node-${d.parent.id}`;
					}
					return c;
				})
				.attr("id", function (d) {
					return d.id;
				})
				.attr("transform", d => {
					return `translate(${d.x},${d.y})`;
				})
				.on("click", d => {
					const isToggle = d3.event.target.closest("div.toggle-child") ? true : false;
					if (isToggle) {
						this.toggleChildren(d);
					} else {
						this.$emit("node-click", d);
						if (this.highlightPathOn === "click") {
							if (d.id !== this.highlightedNodeId) {
								this.highlightedNodeId = d.id;
								this.highlightPath(d, this.nodes);
							} else {
								this.highlightedNodeId = null;
								this.removeHighlight();
							}
						}
					}
				})
				.on("wheel", d => {
					d3.event.stopPropagation();
					d3.event.preventDefault();
					// console.log(d3.event.deltaY);
					if (d.parent) {
						// console.log("offset " + d.parent.offset, " max " + d.parent.max);
						if (d3.event.deltaY > 0) {
							d.parent.offset += 1;
						} else {
							d.parent.offset -= 1;
						}
						this.init(true);
					} else {
						// eslint-disable-next-line no-console
						console.log("null parent");
					}
				})
				.on("mouseover", d => {
					if (this.highlightPathOn === "hover") {
						this.highlightedNodeId = d.id;
						this.highlightPath(d, this.nodes);
					}

					// if (this.scrollTimeout) {
					// 	clearTimeout(this.scrollTimeout);
					// }
					if (d.parent && d.parent.hasScrollbar) {
						d3.select(`#${d.parent.id}-scroll-track`).attr("visibility", "visible");
						d3.select(`#${d.parent.id}-scroll`).attr("visibility", "visible");
					}
				})
				.on("mouseleave", d => {
					if (this.highlightPathOn === "hover") {
						this.highlightedNodeId = null;
						this.removeHighlight();
					}
					// this.scrollTimeout = setTimeout(() => {
					if (d.parent && d.parent.hasScrollbar) {
						d3.select(`#${d.parent.id}-scroll-track`).attr("visibility", "hidden");
						d3.select(`#${d.parent.id}-scroll`).attr("visibility", "hidden");
					}
					// }, 500);
				})
				.on("contextmenu", d => {
					this.handleRightClick(d);
				});
			/**
			 * Adds foreign Object to support native html
			 */
			nodeEnter
				.append("foreignObject")
				.attr("width", d => {
					if (d.isChildNode) {
						return this.childNodeSize.width;
					}
					return this.nodeSize.width;
				})
				.attr("height", d => {
					if (d.isChildNode) {
						return this.childNodeSize.height;
					}
					return this.nodeSize.height;
				})
				.html(d => {
					let size = { ...this.nodeSize };
					if (d.isChildNode) {
						size = { ...this.childNodeSize };
					}
					const propsData = { d, size, dupnames: this.dupnames };
					const nodeInstance = new this.nodeComponent({
						propsData
					});
					return nodeInstance.$mount().$el.outerHTML;
				});

			this.renderScrollbars();

			if (
				(this.viewBoxWidth > this.size.width || this.viewBoxHeight > this.size.height) &&
				this.isSafari()
			) {
				// if (this.safariTimeout) {
				// 	clearTimeout(this.safariTimeout);
				// }
				// this.safariTimeout = setTimeout(() => {
				const sc = this.size.width / this.viewBoxWidth;
				this.scaleForSafari = sc;
				document
					.querySelectorAll(".lineage-node-wrapper .node-icon-container > *")
					.forEach(elem => {
						elem.style.transform = ` scale(${sc}) `;
						elem.style["transform-origin"] = "0% 0% 0px";
					});

				const t = d3.zoomTransform(this.container.node());
				this.zoomContainer(t);
				this.svg.dispatch("click");
				// }, 500);
			}
			/** End : plotting nodes */
		},

		handleRightClick(d) {
			d3.event.stopPropagation();
			d3.event.preventDefault();

			//	const d = d3.select(d3.event.target).datum();

			// console.log("Right click", d);
			this.togglePopOver(d);
		},
		togglePopOver(d = {}) {
			document.querySelectorAll("[popover-for]").forEach(el => {
				el.style.display = "none";
			});
			const target = document.querySelector(`#${d.id}`);
			const content = this.$refs["lineage-popover"]; //document.querySelector("[popover-for='" + d.id + "'],[popover-for='all']");
			if (content) {
				content.style.display = "block";
			}
			if (target && content && this.currentPopper == null) {
				this.popoverDataNode = d;
				this.$emit("node-right-click", d);
				this.overlay = true;
				this.currentPopper = createPopper(target, content, {
					placement: "auto-end",
					strategy: "fixed"
					// modifiers: [
					// 	{
					// 		name: "offset",
					// 		options: {
					// 			offset: [this.nodeSize.width / 2 + 100, -this.nodeSize.height]
					// 		}
					// 	}
					// ]
				});
			} else {
				this.popoverDataNode = {};
				this.overlay = false;
				if (this.currentPopper) {
					this.currentPopper.destroy();
					this.currentPopper = null;
				}
				if (content) {
					content.style.display = "none";
				}
			}
		},

		/**
		 * To check if Browser is Safari
		 */
		isSafari() {
			return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
		},
		/**
		 * Generate random ID for each Instance
		 */
		generateId() {
			let id = "";
			const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";
			const charsLength = chars.length;
			for (let i = 0; i < 10; i++) {
				id += chars.charAt(Math.floor(Math.random() * charsLength));
			}
			return id;
		}
	}
});
</script>
<style lang="scss">
div.flow-lineage-container {
	align-self: stretch;
	width: 100%;
	height: 100%;
	background-color: var(--dashboard-background);
	.flow-lineage-header {
		background-color: var(--dashboard-background);
	}
	.flow-lineage-chart {
		align-self: stretch;
		width: 100%;
		height: calc(100% - 122px);
		overflow: hidden;

		> svg {
			background-color: var(--dashboard-background);
		}
	}

	&:active {
		cursor: grab;
	}

	.link-arrow {
		cursor: pointer;
		// dominant-baseline: central;
		textPath {
			font-size: 24px;
			&:hover {
				// filter: drop-shadow(2px 2px 2px var(--dashboard-primary));
				fill: var(--dashboard-primary);
			}
		}
	}
	.link-overlay {
		opacity: 0;
		&.hover {
			opacity: 0.5;
		}
	}
	.link {
		&:hover {
			stroke: var(--dashboard-primary);
			// -webkit-filter: drop-shadow(3px 3px 2px var(--dashboard-primary));
			// filter: drop-shadow(2px 2px 2px var(--dashboard-primary));
		}
	}

	path.lineage-highlight {
		// stroke: var(--dashboard-primary);
		opacity: 1;
	}

	.lineage-lowlight {
		opacity: 0.2;
		.node-i-wrapper {
			opacity: 0.2;
		}
	}
	.node-popover {
		display: none;
	}

	> div.node-lineage-overlay {
		content: "";
		position: fixed;
		top: 0px;
		left: 0px;

		background-color: #000;
		transition: opacity 0.3s linear;
		z-index: 103;
		opacity: 0;
		pointer-events: none;
	}
	> div.node-lineage-overlay {
		&[open] {
			opacity: 0.7;
			pointer-events: auto;
			width: 100%;
			height: 100%;
		}
	}
	> *:not(div.node-lineage-overlay) {
		z-index: 104;
	}

	.duplicate-indicator {
		width: 16px;
		height: 16px;
		border-radius: 8px;
		background-color: var(--color1-200);
		color: var(--dashboard-background);
		font-size: 10px;
		line-height: 10px;
		font-weight: bold;
		text-transform: uppercase;
		display: inline-flex;
		justify-content: center;
		align-items: center;
		font-family: var(--body-font);
	}
	.scroll,
	.scroll-track {
		opacity: 0.8;
	}
	.pagination-footer.shadow {
		-webkit-filter: drop-shadow(0px -30px 30px rgba(0, 0, 0, 0.8));
		filter: drop-shadow(0px -30px 30px rgba(0, 0, 0, 0.8));
	}
	.pagination-message {
		font-size: 11px;
		font-weight: 500;
		font-family: var(--body-font);
		text-transform: uppercase;
	}
	.pagination-dots {
		font-size: 18px;
		font-weight: bolder;
		fill: var(--dashboard-font-dark);
	}
	.pagination-plus-more {
		font-size: 12px;
		font-weight: 400;
		font-family: var(--body-font);
		fill: var(--dashboard-font-dark);
	}
}

@media not all and (min-resolution: 0.001dpcm) {
	@supports (-webkit-appearance: none) {
		div.flow-lineage-container {
			foreignObject {
				div.lineage-node-wrapper {
					// transform: scale(0.7);
					// position: fixed;
					transform-origin: 0% 0%;
				}
			}
		}
	}
}
</style>
